/**
 * Broadcast event banners — GOAL / FOUL / cards / OFFSIDE / PENALTY / HT / FT.
 * (§5, D0 presentation; user playtest feedback: match events must be LOUD.)
 *
 * Same discipline as hud.ts: plain DOM, its own timers, NEVER drawn from the
 * game loop. The loop only APPENDS facts; this module animates on its own
 * clock (a 150ms drain timer + CSS animations doing the actual motion).
 *
 * Two feeds, because the two match paths know different amounts:
 *
 *   1. `pushEvents(stepResult.events)` — the OFFLINE/practice loop owns the
 *      authoritative StepResult, so it can report scorer slot, foul severity
 *      (yellow/red card), and the house-rule PUNCH/KICK events. game/practice.ts
 *      calls this right after step(); the call just copies a few fields into a
 *      queue.
 *
 *   2. The PHASE WATCHER — online, rule events happen on the server and are
 *      not on the wire; what the client does get is `match.phase` in every
 *      snapshot. A 10Hz timer watches HudState.phase transitions and raises
 *      the banners phases imply (GOAL!, OFFSIDE, PENALTY, FREE KICK, HALF
 *      TIME, FULL TIME). Practice has both feeds; a kind-keyed dedupe window
 *      stops the same moment bannering twice.
 *
 * The referee "voice" is text-only here by design — the card graphic + label
 * is the broadcast cue. A 3D referee is a render-layer follow-up, not ours.
 */

import { MatchPhase, type RuleEvent } from '@fg/shared/sim';
import type { HudState } from './hud.js';
import { broadcastNameFor, broadcastNameOr } from './playerNames.js';

export interface Banners {
  /** Append authoritative rule events (practice/offline path). Cheap: copies
   *  a few fields per event; drawing happens on this module's own timer. */
  pushEvents(events: readonly RuleEvent[]): void;
  /** §5: a brief transient UI message (e.g. a team command's feedback). Not a
   *  rule event — bypasses the per-kind dedupe so distinct commands both show. */
  pushText(title: string): void;
  dispose(): void;
}

type BannerKind =
  | 'goal' | 'foul' | 'yellow' | 'red' | 'offside' | 'penalty'
  | 'freekick' | 'halftime' | 'fulltime' | 'punch' | 'handball' | 'restart' | 'command';

interface QueuedBanner {
  kind: BannerKind;
  title: string;
  sub: string;
  /** null = no card graphic. Cards are disabled (owner, 2026-08-17: "give
   *  only fouls") — nothing in this file enqueues 'yellow'|'red' any more, so
   *  this is always null in practice. Kept, not narrowed to `null`, so the
   *  card-plane CSS/render path below stays intact if cards ever return. */
  card: 'yellow' | 'red' | null;
  holdMs: number;
  big: boolean;
}

/** Re-raising the same kind within this window is the same moment, not news. */
const DEDUPE_MS = 3000;
const DRAIN_INTERVAL_MS = 150;
const EXIT_ANIM_MS = 320;

export function mountBanners(container: HTMLElement, state: HudState): Banners {
  const root = document.createElement('div');
  root.className = 'fgban';
  container.appendChild(root);

  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);

  const queue: QueuedBanner[] = [];
  const lastShownMs: Partial<Record<BannerKind, number>> = {};
  let showing = false;
  let disposed = false;

  const teamName = (team: number): string => (team === 0 ? state.nameA : team === 1 ? state.nameB : '');

  const enqueue = (b: QueuedBanner): void => {
    const now = performance.now();
    const last = lastShownMs[b.kind];
    if (last !== undefined && now - last < DEDUPE_MS) return;
    lastShownMs[b.kind] = now;
    // Bound the queue — a pile-up of stale banners is worse than dropping some.
    if (queue.length >= 4) queue.shift();
    queue.push(b);
  };

  /** Marks a kind as recently-shown WITHOUT queueing — used so an event-fed
   *  banner suppresses the phase-fed echo of the same whistle. */
  const suppress = (kind: BannerKind): void => {
    lastShownMs[kind] = performance.now();
  };

  const show = (b: QueuedBanner): void => {
    showing = true;
    const el = document.createElement('div');
    el.className = `fgban-item${b.big ? ' fgban-big' : ''}`;
    if (b.card) {
      const card = document.createElement('span');
      card.className = `fgban-card fgban-card-${b.card}`;
      el.appendChild(card);
    }
    const text = document.createElement('span');
    text.className = 'fgban-text';
    const title = document.createElement('span');
    title.className = 'fgban-title';
    title.textContent = b.title;
    text.appendChild(title);
    if (b.sub) {
      const sub = document.createElement('span');
      sub.className = 'fgban-sub';
      sub.textContent = b.sub;
      text.appendChild(sub);
    }
    el.appendChild(text);
    root.appendChild(el);

    window.setTimeout(() => {
      el.classList.add('fgban-out');
      window.setTimeout(() => {
        el.remove();
        showing = false;
      }, EXIT_ANIM_MS);
    }, b.holdMs);
  };

  // ── Feed 1: authoritative StepResult events (practice) ────────────────────
  const pushEvents = (events: readonly RuleEvent[]): void => {
    if (disposed) return;
    for (const e of events) {
      switch (e.type) {
        case 'GOAL':
          suppress('goal'); // beat the phase watcher to the same moment
          queue.push({
            kind: 'goal',
            title: 'GOAL!',
            sub: `${teamName(e.team)}${e.player >= 0 ? ` — #${e.player}` : ''}`,
            card: null,
            holdMs: 2800,
            big: true,
          });
          lastShownMs['goal'] = performance.now();
          break;
        case 'FOUL': {
          // CARDS ARE DISABLED (owner, 2026-08-17: "remove the red card thing
          // and yellow card from the game give only fouls"). Every severity
          // now shows the same FOUL banner — `e.severity` is no longer read
          // here at all, and `e.card` (rules/fouls.ts) is always None anyway.
          suppress('freekick'); // the restart banner would just echo this
          // LATCH BOTH SIDES OF THE FOUL. The owner asked for "the player name
          // who won foul", and the victim's id lives for exactly this one tick —
          // MatchState keeps the fouler and the favoured team, never the fouled
          // MAN. The restart banner that follows a second later reads it back
          // out of HudState. See HudState.foulVictim.
          const victim = e.victim !== undefined && e.victim >= 0 ? e.victim : -1;
          state.foulVictim = victim;
          state.foulOffender = e.player >= 0 ? e.player : -1;
          // Both names when both are known: who did it, and who won it.
          const sub = victim >= 0
            ? (e.player >= 0
              ? `${broadcastNameOr(e.player)} on ${broadcastNameOr(victim)}`
              : `on ${broadcastNameOr(victim)}`)
            : (e.player >= 0 ? `by ${broadcastNameOr(e.player)}` : '');
          enqueue({
            kind: 'foul', title: 'FOUL', card: null, big: false, holdMs: 1800, sub,
          });
          break;
        }
        case 'PUNCH':
        case 'KICK':
          suppress('freekick');
          if (e.victim !== undefined && e.victim >= 0) state.foulVictim = e.victim;
          if (e.player >= 0) state.foulOffender = e.player;
          enqueue({
            kind: 'punch',
            title: e.type === 'PUNCH' ? 'VIOLENT CONDUCT' : 'VIOLENT KICK',
            sub: e.player >= 0 ? `${broadcastNameOr(e.player)} — always a foul` : '',
            card: null, big: false, holdMs: 2000,
          });
          break;
        case 'OFFSIDE':
          enqueue({ kind: 'offside', title: 'OFFSIDE', sub: teamName(1 - e.team), card: null, big: false, holdMs: 1900 });
          break;
        case 'HANDBALL':
          enqueue({ kind: 'handball', title: 'HANDBALL', sub: '', card: null, big: false, holdMs: 1800 });
          break;
        case 'HALF_TIME':
          enqueue({ kind: 'halftime', title: 'HALF TIME', sub: `${state.nameA} ${state.scoreA} – ${state.scoreB} ${state.nameB}`, card: null, big: true, holdMs: 2600 });
          break;
        case 'FULL_TIME':
          enqueue({ kind: 'fulltime', title: 'FULL TIME', sub: `${state.nameA} ${state.scoreA} – ${state.scoreB} ${state.nameB}`, card: null, big: true, holdMs: 2600 });
          break;
        default:
          break; // BALL_OUT_* / BACK_PASS / SIX_SECOND / ADVANTAGE: too minor to banner
      }
    }
  };

  /**
   * "OKONKWO TO TAKE IT", or '' when nobody is named.
   *
   * Empty rather than "#7" for a restart caption: the number-only form was the
   * pre-names behaviour and it is kept as the FALLBACK inside broadcastNameOr
   * for the foul lines, where identifying the man is the whole point. On a
   * routine restart a bare "#7" adds nothing over the title, so the caption
   * simply stays quiet — which is also what happens before
   * `configureMatchNames` has been called, e.g. on a club with no modelled cast.
   */
  const takerSub = (): string => {
    const name = state.restartTaker >= 0 ? broadcastNameFor(state.restartTaker) : null;
    return name === null ? '' : `${name} TO TAKE IT`;
  };

  /** "WON BY HAYES", from the latched victim of the foul that caused this restart. */
  const wonFoulSub = (): string => {
    const name = state.foulVictim >= 0 ? broadcastNameFor(state.foulVictim) : null;
    return name === null ? '' : `WON BY ${name}`;
  };

  // ── Feed 2: phase transitions (works online, where events never arrive) ───
  let lastPhase: MatchPhase = state.phase;
  const onPhase = (phase: MatchPhase): void => {
    switch (phase) {
      case MatchPhase.Goal:
        enqueue({ kind: 'goal', title: 'GOAL!', sub: '', card: null, holdMs: 2800, big: true });
        break;
      case MatchPhase.Offside:
        enqueue({ kind: 'offside', title: 'OFFSIDE', sub: '', card: null, holdMs: 1900, big: false });
        break;
      case MatchPhase.Penalty:
        enqueue({ kind: 'penalty', title: 'PENALTY!', sub: '', card: null, holdMs: 2600, big: true });
        break;
      case MatchPhase.FreeKick:
        // A free kick always follows a foul, so the interesting name is the man
        // who WON it, not the man about to take it.
        enqueue({ kind: 'freekick', title: 'FREE KICK', sub: wonFoulSub(), card: null, holdMs: 1600, big: false });
        break;
      // Ball-out restarts (§5.2) were deliberately un-bannered on the richer
      // event feed above ("too minor to banner") — correct for a big dramatic
      // card, but that left a real gap: the sim correctly stops play and stages
      // a restart, yet nothing on screen ever said so. A ball that vanishes off
      // the pitch and reappears at a throw-in spot with zero signal reads as
      // "the game didn't notice it went out" even though it did. These are
      // deliberately the quietest, shortest banners in the file — routine
      // restarts, not moments — same weight class as FREE KICK, not GOAL.
      // ── …and now they say WHO (owner, 2026-08-09) ─────────────────────────
      //
      // *"show the player name who has to do the throw in […] and same for
      // corner"*. The taker is picked by `pickRestartTaker` inside the same
      // `beginRestart` call that flips the phase, so by the time this 10 Hz
      // sampler sees the new phase `restartTaker` is already valid — no race,
      // no need to wait a tick for it. The hold is lengthened from 1100 ms
      // because a caption with a name in it now has something to read.
      case MatchPhase.ThrowIn:
        enqueue({ kind: 'restart', title: 'THROW-IN', sub: takerSub(), card: null, holdMs: 1800, big: false });
        break;
      case MatchPhase.Corner:
        enqueue({ kind: 'restart', title: 'CORNER', sub: takerSub(), card: null, holdMs: 1800, big: false });
        break;
      case MatchPhase.GoalKick:
        enqueue({ kind: 'restart', title: 'GOAL KICK', sub: takerSub(), card: null, holdMs: 1800, big: false });
        break;
      case MatchPhase.HalfTime:
        enqueue({ kind: 'halftime', title: 'HALF TIME', sub: `${state.nameA} ${state.scoreA} – ${state.scoreB} ${state.nameB}`, card: null, holdMs: 2600, big: true });
        break;
      case MatchPhase.FullTime:
        enqueue({ kind: 'fulltime', title: 'FULL TIME', sub: `${state.nameA} ${state.scoreA} – ${state.scoreB} ${state.nameB}`, card: null, holdMs: 2600, big: true });
        break;
      default:
        break;
    }
  };

  // 10Hz watch + drain. Phase flips shorter than 100ms can be missed by a
  // sampler by nature; every phase this module banners holds for seconds
  // (restart setup windows), so sampling is sound — and the practice path
  // doesn't rely on it at all (it has the real events).
  const timer = window.setInterval(() => {
    if (state.phase !== lastPhase) {
      onPhase(state.phase);
      lastPhase = state.phase;
    }
    if (!showing && queue.length > 0) show(queue.shift()!);
  }, DRAIN_INTERVAL_MS);

  const pushText = (title: string): void => {
    if (disposed || !title) return;
    if (queue.length >= 4) queue.shift();
    queue.push({ kind: 'command', title, sub: '', card: null, holdMs: 900, big: false });
  };

  return {
    pushEvents,
    pushText,
    dispose(): void {
      disposed = true;
      window.clearInterval(timer);
      root.remove();
      style.remove();
    },
  };
}

/**
 * Center-top banner strip. Skew + counter-skew matches the hud.ts score bug,
 * so events and the bug read as one broadcast package. pointer-events: none —
 * a banner must never eat a click mid-match.
 */
const CSS = `
.fgban {
  position: fixed; top: 12vh; left: 50%; transform: translateX(-50%);
  z-index: 18; pointer-events: none;
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  font-family: 'Segoe UI', 'Helvetica Neue', system-ui, sans-serif;
}
.fgban-item {
  display: flex; align-items: center; gap: 12px;
  padding: 9px 26px;
  background: linear-gradient(180deg, rgba(32,37,50,0.97) 0%, rgba(14,17,25,0.95) 100%);
  border-left: 4px solid #ffd94a;
  border-radius: 3px;
  transform: skewX(-10deg);
  box-shadow: 0 6px 24px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08);
  animation: fgbanIn 260ms cubic-bezier(0.22, 1.2, 0.36, 1);
}
.fgban-big { padding: 12px 38px; border-left-width: 5px; }
.fgban-out { animation: fgbanOut ${EXIT_ANIM_MS}ms ease-in forwards; }
.fgban-text { display: flex; flex-direction: column; transform: skewX(10deg); text-align: center; }
.fgban-title {
  color: #fff; font-weight: 900; letter-spacing: 3px; font-size: 17px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.6);
}
.fgban-big .fgban-title { font-size: 26px; letter-spacing: 5px; color: #ffd94a; }
.fgban-sub { color: rgba(255,255,255,0.75); font-size: 11px; letter-spacing: 1.2px; margin-top: 1px; }
.fgban-card {
  width: 15px; height: 21px; border-radius: 2px; transform: skewX(10deg) rotate(-6deg);
  box-shadow: 0 2px 6px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.35);
  animation: fgbanCard 420ms cubic-bezier(0.22, 1.4, 0.36, 1);
}
.fgban-card-yellow { background: linear-gradient(180deg, #ffe27a, #f0b90b); }
.fgban-card-red { background: linear-gradient(180deg, #ff6a5e, #d61f0f); }
@keyframes fgbanIn {
  from { opacity: 0; transform: skewX(-10deg) translateY(-26px) scale(0.92); }
  to   { opacity: 1; transform: skewX(-10deg) translateY(0) scale(1); }
}
@keyframes fgbanOut {
  to { opacity: 0; transform: skewX(-10deg) translateY(-14px); }
}
@keyframes fgbanCard {
  from { transform: skewX(10deg) rotate(-40deg) translateY(-18px); opacity: 0; }
  to   { transform: skewX(10deg) rotate(-6deg) translateY(0); opacity: 1; }
}
`;
